import { Component } from '@theme/component';

/**
 * <q-read-more> — collapses long content (supplier descriptions with a
 * dozen infographics) to a preview with a fade and a "Read more" button.
 *
 * Markup contract (refs, Horizon's Component pattern):
 *   ref="content"   the collapsible region (gets an id for aria-controls)
 *   ref="toggle"    the button   on:click="/toggle"
 *   data-collapsed-height="640"  preview height in px
 *   data-label-more / data-label-less   button text
 *
 * Content shorter than the preview (plus a small allowance, so a button
 * never reveals three lines) is shown in full with no button. Images that
 * load later can grow the content, so the check re-runs on resize.
 * Collapsing scrolls the start of the block back into view, so the shopper
 * isn't left far below where they were reading.
 *
 * @extends {Component<{ content: HTMLElement, toggle: HTMLButtonElement }>}
 */
class ReadMore extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #observer;

  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.refs.content);
    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  get #collapsedHeight() {
    return Number(this.dataset.collapsedHeight) || 640;
  }

  toggle() {
    this.#expanded = !this.#expanded;
    this.#apply();
    if (!this.#expanded && this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  #measure() {
    const { content, toggle } = this.refs;
    const needed = content.scrollHeight > this.#collapsedHeight + 120;
    toggle.hidden = !needed;
    this.toggleAttribute('data-collapsible', needed);
    this.#apply();
  }

  #apply() {
    const { content, toggle } = this.refs;
    const collapsed = this.hasAttribute('data-collapsible') && !this.#expanded;
    this.toggleAttribute('data-collapsed', collapsed);
    content.style.maxHeight = collapsed ? `${this.#collapsedHeight}px` : '';
    toggle.setAttribute('aria-expanded', String(!collapsed));
    toggle.textContent = (collapsed ? this.dataset.labelMore : this.dataset.labelLess) ?? '';
  }
}

if (!customElements.get('q-read-more')) {
  customElements.define('q-read-more', ReadMore);
}
